/** Objet retenu sous le pointeur (MapCanvas), hors mode édition et en mode édition du fuseau (docs/14 §2.3).
 *
 *  Hors édition, par ordre de priorité : unité, ligne de coordination (LIMA), ligne de la ZO (docs/15 §4.3 : rang
 *  1,5, après la LIMA qu'elle croise et avant le pont), pont, point de passage obligé, contour ou ligne d'un
 *  graphique tactique, autres objets du camp (routes, cases), et en dernier la surface d'une zone (un pont ou une
 *  route reste cliquable à l'intérieur d'une zone). À priorité égale, l'objet dessiné au-dessus.
 *
 *  En édition, les poignées passent devant tout : sommet de limite (−3), extrémité ou courbure de LIMA et
 *  extrémité d'une ligne de la ZO (−2), poignée de milieu (−1), puis unité (0), LIMA entière (1), ligne de la ZO
 *  entière (1,5) et le reste inchangé. Les poignées sont dessinées sans
 *  interrogation du moteur : elles se saisissent dans un rayon de 12 px (18 au doigt), la plus proche l'emporte
 *  (`pickHandle`, handles.ts). */

import type { HandleHit } from "./handles";

/** Identifiants des couches interrogées (copiés ici pour garder ce module pur et testable sans moteur). */
export const PICK_LAYERS = {
  unit: "units-symbol",
  line: "corridor-lines-hit",
  measure: "zo-measures-hit",
  bridge: "bridges-symbol",
  chokepoint: "chokepoints-circle",
  graphicEdge: "graphics-hit-edge",
  graphicFill: "graphics-fill",
  annotation: "annotations-symbol",
} as const;

/** Rang de chaque couche (plus petit : prioritaire) ; absente : 5. */
export const PICK_RANK: Readonly<Record<string, number>> = {
  [PICK_LAYERS.unit]: 0,
  // Annotation APP-6 : juste après les unités (un symbole posé sur la LIMA reste saisissable).
  [PICK_LAYERS.annotation]: 0.5,
  [PICK_LAYERS.line]: 1,
  [PICK_LAYERS.measure]: 1.5,
  [PICK_LAYERS.bridge]: 2,
  [PICK_LAYERS.chokepoint]: 3,
  [PICK_LAYERS.graphicEdge]: 4,
  [PICK_LAYERS.graphicFill]: 6,
};

/** Rangs du mode édition : les poignées (−3 à −1, `handleRank`) précèdent les mêmes rangs qu'hors édition. */
export const EDIT_PICK_RANK: Readonly<Record<string, number>> = {
  vertex: -3,
  limaEnd: -2,
  limaBend: -2,
  measureEnd: -2,
  mid: -1,
  limaMid: -1,
  ...PICK_RANK,
};

export const DEFAULT_RANK = 5;

export interface PickFeature {
  layer?: { id?: string };
  properties?: Record<string, unknown> | null;
  geometry?: unknown;
}

/** Entité prioritaire parmi celles touchées ; à rang égal, la première (dessinée au-dessus). */
export function topFeature<F extends PickFeature>(features: readonly F[] | undefined): F | undefined {
  let best: F | undefined;
  let bestRank = Infinity;
  for (const feature of features ?? []) {
    const rank = PICK_RANK[feature.layer?.id ?? ""] ?? DEFAULT_RANK;
    if (rank < bestRank) {
      best = feature;
      bestRank = rank;
    }
  }
  return best;
}

export type Picked<F extends PickFeature> =
  | { kind: "handle"; hit: HandleHit; rank: number }
  | { kind: "feature"; feature: F; rank: number }
  | null;

/** Cible sous le pointeur, celle que MapCanvas retient au début d'un geste, au clic et au double-clic : en mode
 *  édition, la poignée saisie (`pickHandle`) passe devant toute entité ; sinon l'entité prioritaire. Les entités
 *  peuvent être données à la demande (`() => features`) : l'interrogation du moteur est évitée quand une poignée
 *  l'emporte. */
export function pickTarget<F extends PickFeature>(
  handle: HandleHit | null,
  features: readonly F[] | undefined | (() => readonly F[] | undefined),
  editing: boolean,
): Picked<F> {
  if (editing && handle) return { kind: "handle", hit: handle, rank: EDIT_PICK_RANK[handle.handle.kind] ?? -1 };
  const feature = topFeature(typeof features === "function" ? features() : features);
  if (!feature) return null;
  return { kind: "feature", feature, rank: PICK_RANK[feature.layer?.id ?? ""] ?? DEFAULT_RANK };
}
